import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import toast from "components/partials/toast/toast";

import { getCampaign } from "generated/api/campaign";

import { useCurrentClient } from "hooks/api/use-clients";
import { useCampaignFromTemplateContext } from "components/modals/create-campaign-from-template-modal/context/campaign-from-template-context";

import { getCampaignPaths } from "utilities/app-routes";

import { getSelectedTemplates } from "components/modals/create-campaign-from-template-modal/create-campaign-from-template-modal.helper";

import type { CreateCampaignFromTemplateFormData } from "components/modals/create-campaign-from-template-modal/create-campaign-from-template-modal.types";

import {
  MondayCampaignRequestProgramType,
  MondayCampaignRequestCampaignObjective,
  MondayCampaignRequestAudiencesItem,
  MondayCampaignRequest,
  MondayCampaignTemplateDTO,
  MondayCampaignTemplateItem,
} from "generated/schemas";

export const useCreateCampaignFromTemplate = () => {
  const { campaignTemplates, setIsOpen } = useCampaignFromTemplateContext();
  const currentClient = useCurrentClient();
  const navigate = useNavigate();

  const createCampaign = useCallback(
    async (formData: CreateCampaignFromTemplateFormData) => {
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      let requestCompleted = false;

      try {
        const clientId = currentClient.id;
        const campaign = getCampaign();

        // Filter templates to only include selected ones (where value is true)
        // Look up full template objects by ID from campaignTemplates
        const selectedTemplates = getSelectedTemplates(formData.templates || {}, campaignTemplates);

        const mondayCampaignTemplates = buildMondayCampaignTemplatesPayload(
          campaignTemplates,
          formData.templates || {},
        );

        const request: MondayCampaignRequest = {
          programType: formData.programType as MondayCampaignRequestProgramType,
          // Initial campaign name from the form; will be overridden by Monday formula name if provided
          name: formData.campaignName,
          campaignObjective: formData.campaignObjective as MondayCampaignRequestCampaignObjective,
          audiences: formData.audiences as MondayCampaignRequestAudiencesItem[],
          cdnSubdirectoryName: formData.cdnSubdirectoryName,
          transferStudentStatusFlag: formData.transferStudentStatusFlag ?? false,
          mondayDetails:
            mondayCampaignTemplates.length > 0
              ? {
                  campaignTemplates: mondayCampaignTemplates,
                }
              : undefined,
        };

        // Set a timer to show "operation in progress" message if request takes too long
        timeoutId = setTimeout(() => {
          if (!requestCompleted) {
            toast.info({
              data: {
                title: "Operation in progress",
                message:
                  "The operation is still processing. Please check back in a couple of minutes.",
              },
              autoClose: false,
            });
            setIsOpen(false);
            requestCompleted = true;
          }
        }, 119000); // 1 minute 59 seconds

        const createdCampaign = await campaign.createCampaignFromMondayTemplates(
          { clientId },
          request,
        );

        if (timeoutId) clearTimeout(timeoutId);
        requestCompleted = true;

        if (createdCampaign?.id) {
          toast.success({
            data: {
              title: "Created campaign",
            },
          });

          const touchpointCount = selectedTemplates.length;
          toast.success({
            data: {
              title: touchpointCount === 1 ? "Created creative" : "Created creatives",
            },
          });

          const { campaignCreativePath } = getCampaignPaths({
            clientId,
            campaignId: createdCampaign.id,
          });
          navigate(campaignCreativePath);
        }
        setIsOpen(false);
      } catch (error) {
        if (timeoutId) clearTimeout(timeoutId);

        // Only show error if we haven't already shown the "in progress" message
        if (!requestCompleted) {
          console.error("Failed to create campaign with touchpoints:", error);
          toast.error({
            data: {
              title: "Failed to create campaign",
            },
          });
        }
      }
    },
    [campaignTemplates, currentClient, navigate, setIsOpen],
  );

  return { createCampaign };
};

/**
 * Build the mondayDetails.campaignTemplates payload from the selected touchpoint templates.
 * Groups selected touchpoints under their parent campaign template.
 */
const buildMondayCampaignTemplatesPayload = (
  campaignTemplates: MondayCampaignTemplateDTO[],
  selectedTemplateMap: Record<string, boolean>,
): MondayCampaignTemplateItem[] => {
  const result: MondayCampaignTemplateItem[] = [];

  for (const campaignTemplate of campaignTemplates) {
    const touchpoints =
      campaignTemplate.touchpointTemplates?.filter((tp) => tp.id && selectedTemplateMap[tp.id]) ??
      [];

    if (!touchpoints.length) {
      continue;
    }

    if (
      typeof campaignTemplate.id === "string" &&
      typeof campaignTemplate.campaignTemplate === "string"
    ) {
      result.push({
        campaignTemplateId: campaignTemplate.id,
        campaignTemplateName: campaignTemplate.campaignTemplate,
        touchpointTemplates: touchpoints,
      });
    }
  }

  return result;
};
